import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MdReceipt, MdPerson, MdPlace, MdCreditCard } from "react-icons/md";

import styles from "./OrderDetails.module.scss";
import { useOrderDetails } from "./useOrderDetails";
import { PageHeader } from "../../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../../common/components/emptyState/EmptyState";
import { clusterRoute } from "../../../../common/utils/constants";
import {
  getOrderStatusText,
  getStatusTextColor,
  getPaymentMethodText,
  getPaymentStatusText,
  getPaymentStatusColor,
  getDeliveryTypeText,
  getTaskStatusText,
} from "../../../../common/utils/functions/enumToText";
import {
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  DeliveryType,
  TaskStatus,
} from "../../../../common/types/enums";

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export const OrderDetails: React.FC = () => {
  const { order, ready } = useOrderDetails();

  if (!ready) return null;

  if (!order) {
    return (
      <div className={styles.Page}>
        <EmptyState
          icon={MdReceipt}
          title="Заказ не найден"
          description="Возможно, он был удалён."
        />
      </div>
    );
  }

  const cluster = order.cluster;
  const items = order.items ?? [];
  const task = order.tasks?.[0];

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`Заказ #${order.id} — ЛОГМОЛЛ`}</title>
      </Helmet>

      <PageHeader
        title={`Заказ #${order.id}`}
        subtitle={formatDate(order.createdAt)}
      />

      <div className={styles.StatusHero}>
        <span
          className={styles.StatusHeroBg}
          style={{ color: getStatusTextColor(order.status as OrderStatus) }}
          aria-hidden="true"
        >
          {getOrderStatusText(order.status as OrderStatus)}
        </span>
        <span
          className={styles.StatusHeroLabel}
          style={{ color: getStatusTextColor(order.status as OrderStatus) }}
        >
          {getOrderStatusText(order.status as OrderStatus)}
        </span>
      </div>

      <div className={styles.Content}>
        {cluster && (
          <Link to={clusterRoute(cluster.id)} className={styles.ClusterLink}>
            <img
              src={`https://${cluster.domain}/icons/logo512.png`}
              alt={cluster.name}
              className={styles.ClusterLogo}
            />
            <span className={styles.ClusterName}>{cluster.name}</span>
          </Link>
        )}

        {items.length > 0 && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Состав заказа</h2>
            <div className={styles.List}>
              {items.map((item, index) => (
                <div key={item.id ?? index} className={styles.ItemRow}>
                  <span className={styles.ItemName}>{item.name}</span>
                  <span className={styles.ItemQty}>× {item.quantity}</span>
                  <span className={styles.ItemPrice}>{item.total} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {order.store && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>
              {getDeliveryTypeText(order.deliveryType as DeliveryType)}
            </h2>
            <div className={styles.InfoCard}>
              <MdPlace size={22} className={styles.InfoIcon} />
              <div className={styles.InfoText}>
                <span className={styles.InfoName}>{order.store.name}</span>
                <span className={styles.InfoValue}>
                  {order.store.city?.value} {order.store.address?.value}
                </span>
              </div>
            </div>
          </div>
        )}

        {order.fullAddress && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Адрес доставки</h2>
            <div className={styles.InfoCard}>
              <MdPlace size={22} className={styles.InfoIcon} />
              <div className={styles.InfoText}>
                <span className={styles.InfoValue}>
                  {order.fullAddress.city?.value}{" "}
                  {order.fullAddress.address?.value}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.Section}>
          <h2 className={styles.SectionTitle}>Оплата</h2>
          <div className={styles.InfoCard}>
            <MdCreditCard size={22} className={styles.InfoIcon} />
            <div className={styles.InfoText}>
              <span className={styles.InfoName}>
                {getPaymentMethodText(order.paymentMethod as PaymentMethod)}
              </span>
              <span
                className={styles.InfoValue}
                style={{
                  color: getPaymentStatusColor(order.paymentStatus as PaymentStatus),
                }}
              >
                {getPaymentStatusText(order.paymentStatus as PaymentStatus)}
              </span>
            </div>
          </div>
        </div>

        {task?.executor && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Курьер</h2>
            <div className={styles.InfoCard}>
              <MdPerson size={22} className={styles.InfoIcon} />
              <div className={styles.InfoText}>
                <span className={styles.InfoName}>{task.executor.name}</span>
                <span className={styles.InfoValue}>
                  {task.executor.phone_number}
                </span>
                <span className={styles.InfoValue}>
                  {getTaskStatusText(task.status as TaskStatus)}
                </span>
              </div>
            </div>
          </div>
        )}

        {order.notes && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Комментарий</h2>
            <div className={styles.NotesCard}>{order.notes}</div>
          </div>
        )}

        <div className={styles.TotalSection}>
          <div className={styles.TotalRow}>
            <span>Товары</span>
            <span>{order.subtotal} ₽</span>
          </div>
          {order.deliveryFee > 0 && (
            <div className={styles.TotalRow}>
              <span>Доставка</span>
              <span>{order.deliveryFee} ₽</span>
            </div>
          )}
          {order.discountAmount && order.discountAmount > 0 && (
            <div className={styles.TotalRow}>
              <span>Скидка</span>
              <span>-{order.discountAmount} ₽</span>
            </div>
          )}
          {order.bonusesSpent && order.bonusesSpent > 0 && (
            <div className={styles.TotalRow}>
              <span>Списано бонусами</span>
              <span>-{order.bonusesSpent} ₽</span>
            </div>
          )}
          <div className={`${styles.TotalRow} ${styles.Grand}`}>
            <span>Итого</span>
            <span>{order.total} ₽</span>
          </div>
        </div>
      </div>
    </div>
  );
};